import React, { useState } from "react";
import { GitPullRequest, Bot, MessageSquare, FileCode } from "lucide-react";

interface PullRequestPreviewProps {
  title: string;
  author: string;
  status: "open" | "merged" | "closed";
  body: React.ReactNode;
  filesChangedContent?: React.ReactNode;
}

const STATUS_STYLES = {
  open: "bg-primary text-primary-foreground",
  merged: "bg-violet-700 text-white",
  closed: "bg-surface-foreground/20 text-surface-foreground",
};

export function PullRequestPreview({
  title,
  author,
  status,
  body,
  filesChangedContent,
}: PullRequestPreviewProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<"conversation" | "files">("conversation");

  return (
    <div className="rounded-3xl border bg-background/50 overflow-hidden">
      {/* PR Header */}
      <div className="px-6 pt-6 pb-4 border-b">
        <h3 className="text-xl font-bold leading-snug mb-3">{title}</h3>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${STATUS_STYLES[status]}`}
          >
            <GitPullRequest className="size-3.5" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          <span className="text-sm text-surface-foreground/60">
            <span className="font-medium text-foreground">{author}</span>
            <span className="mx-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary font-medium">
              <Bot className="size-3" />
              bot
            </span>
            opened this pull request
          </span>
        </div>
      </div>

      {/* Tab bar */}
      {filesChangedContent && (
        <div className="flex border-b px-2 bg-background/30">
          <button
            onClick={() => setActiveTab("conversation")}
            className={
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors " +
              (activeTab === "conversation"
                ? "border-primary text-foreground"
                : "border-transparent text-surface-foreground/50 hover:text-foreground")
            }
          >
            <MessageSquare className="size-3.5" />
            Conversation
            <span className="px-1.5 py-0.5 rounded-full text-xs bg-surface-foreground/10 tabular-nums">0</span>
          </button>
          <button
            onClick={() => setActiveTab("files")}
            className={
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors " +
              (activeTab === "files"
                ? "border-primary text-foreground"
                : "border-transparent text-surface-foreground/50 hover:text-foreground")
            }
          >
            <FileCode className="size-3.5" />
            Files changed
            <span className="px-1.5 py-0.5 rounded-full text-xs bg-surface-foreground/10 tabular-nums">1</span>
          </button>
        </div>
      )}

      {/* Tab content */}
      {activeTab === "conversation" || !filesChangedContent ? (
        <div className="px-6 py-5">
          <div className="border-l-2 border-primary/30 pl-4">
            <div className="prose-sm max-w-none text-surface-foreground/90 [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-foreground [&_h3]:mt-5 [&_h3]:mb-2 [&_h3:first-child]:mt-0 [&_p]:leading-relaxed [&_p]:mb-3 [&_ul]:my-2 [&_ul]:space-y-1 [&_li]:leading-relaxed">
              {body}
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4">
          {filesChangedContent}
        </div>
      )}
    </div>
  );
}

export function InlineCode({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <code className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[0.8em] text-foreground">
      {children}
    </code>
  );
}
